import authService from './authService'
import { withApiBaseUrl } from '../lib/apiBaseUrl'
import { getPreferredUserIdentifier } from '../utils/userIdentity'
import { handleAuthExpired, handleSessionReplaced } from '../utils/sessionReplacedHandler'

function getAccessToken(): string | null {
  return authService.getAccessToken()
}

function getTenantId(): string {
  const user = authService.getCurrentUser() as { tenantId?: string } | null
  return user?.tenantId || localStorage.getItem('tenantId') || ''
}

async function promotionFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const token = getAccessToken()
  const tenantId = getTenantId()
  const res = await fetch(withApiBaseUrl(path), {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(tenantId ? { 'X-Tenant-ID': tenantId } : {}),
      ...(options?.headers as Record<string, string>),
    },
    credentials: 'include',
  })

  const data = (await res.json().catch(() => ({ success: false, message: `HTTP ${res.status}` }))) as T & {
    code?: string
    message?: string
    error?: string
  }

  if (res.status === 401 && (data as any)?.code === 'SESSION_REPLACED') {
    await handleSessionReplaced()
    throw new Error('SESSION_REPLACED')
  }

  if (res.status === 401 || res.status === 403) {
    const message = String((data as any)?.error || (data as any)?.message || '').toLowerCase()
    if (res.status === 401 || message.includes('invalid token') || message.includes('expired token')) {
      await handleAuthExpired()
      throw new Error('AUTH_EXPIRED')
    }
  }

  if (!res.ok) {
    const err = new Error((data as any)?.message || `HTTP ${res.status}`) as Error & { status?: number }
    err.status = res.status
    throw err
  }

  return data
}

export interface PromotionCampaignItem {
  campaignId?: string
  _id?: string
  name: string
  description?: string
  type?: string
  status?: string
  priority?: number
  display?: {
    title?: string
    subtitle?: string
    description?: string
    imageUrl?: string
    bannerUrl?: string
    iconUrl?: string
    buttonText?: string
    displayOrder?: number
  }
  schedule?: {
    startDate?: string
    endDate?: string
  }
  settings?: {
    bonusType?: string
    bonusAmount?: number
  }
}

interface PromotionDashboardResponse {
  success: boolean
  data?: {
    campaigns?: {
      active?: PromotionCampaignItem[]
    }
  }
}

interface PromotionCampaignListResponse {
  success: boolean
  data?: {
    campaigns?: PromotionCampaignItem[]
  }
}

export interface PromotionApplyResponse {
  success: boolean
  data?: unknown
  message?: string
}

const sortActiveCampaigns = (campaigns: PromotionCampaignItem[]): PromotionCampaignItem[] =>
  [...campaigns].sort((left, right) => {
    const leftOrder = Number(left.display?.displayOrder ?? Number.MAX_SAFE_INTEGER)
    const rightOrder = Number(right.display?.displayOrder ?? Number.MAX_SAFE_INTEGER)
    const leftPriority = Number(left.priority ?? 0)
    const rightPriority = Number(right.priority ?? 0)

    return leftOrder - rightOrder || rightPriority - leftPriority || left.name.localeCompare(right.name)
  })

export async function getPromotionDashboard(): Promise<PromotionCampaignItem[]> {
  try {
    const res = await promotionFetch<PromotionCampaignListResponse>(
      '/api/v1/promotion/campaigns?status=active&limit=20&sortBy=priority&sortOrder=desc'
    )
    if (!res.success || !Array.isArray(res.data?.campaigns)) return []
    return sortActiveCampaigns(
      res.data.campaigns.filter((campaign) => !campaign.status || campaign.status === 'active')
    )
  } catch (error) {
    const status = (error as { status?: number } | undefined)?.status
    if (status && status !== 404) throw error
  }

  const fallback = await promotionFetch<PromotionDashboardResponse>('/api/v1/promotion')
  if (!fallback.success || !fallback.data?.campaigns?.active) return []
  return sortActiveCampaigns(fallback.data.campaigns.active)
}

export async function applyPromotion(promotionId: string, promotionType: 'campaign' | 'bonus'): Promise<PromotionApplyResponse> {
  const user = authService.getCurrentUser() as { id?: string; _id?: string } | null
  const userId = getPreferredUserIdentifier(user)

  const res = await promotionFetch<PromotionApplyResponse>('/api/v1/promotion/apply', {
    method: 'POST',
    body: JSON.stringify({ userId, promotionId, promotionType }),
  })

  return res
}
